/**
 * What a role may do — an AFFORDANCE layer, never an enforcement one.
 *
 * The backend decides. Every mutating route declares a minimum role
 * (api/auth/authz.py, pinned by tests/api/test_role_declaration_invariant.py)
 * and answers 403 regardless of what this file says. Nothing here may be
 * treated as a security boundary: a user who edits the bundle, or calls the API
 * directly, gains exactly nothing.
 *
 * What it IS for: not offering an action that will fail. A viewer shown a
 * "Delete pond" button learns the app is broken; a viewer not shown it learns
 * what their account is for.
 *
 * THE RANKS MIRROR `_ROLE_ORDER` IN api/auth/authz.py AND MUST STAY IN STEP.
 * There is no automated check across the language boundary — if the backend
 * ladder gains a rung, this table is the second place to change and nothing
 * will remind you. Keeping it small and in one file is the mitigation.
 *
 * ⚠️ COMPARE BY RANK, NEVER BY THE ROLE STRING. "viewer" sorts above "operator"
 * and "admin" alphabetically, so any comparison on the names themselves reads
 * the weakest role as the strongest. That mistake produced a fully convincing
 * and completely wrong audit of the backend wiring before it was caught.
 *
 * Deliberately NOT a function of tenant suspension. A suspended tenant is
 * read-only and every write 403s, so hiding the controls would be consistent
 * with this file's purpose — but the product decision for suspension is the
 * opposite one: the customer should SEE what they are losing, which is why that
 * state ships a banner rather than a stripped-down interface. Suspension is
 * explained; a role is simply what the account is.
 */
import type { Role } from '@/api/client'

/**
 * The ladder, weakest first. Single source for "every role that exists" on the
 * frontend; `RANK` below supplies the ordering it agrees with.
 */
export const ROLE_LADDER: readonly Role[] = ['viewer', 'operator', 'admin', 'owner']

const RANK: Record<Role, number> = {
  viewer: 0,
  operator: 1,
  admin: 2,
  owner: 3,
}

function atLeast(role: Role | null, minimum: Role): boolean {
  // A null role means "not signed in, or the session has not resolved yet".
  // False, not true: the brief window before the boot probe answers must not
  // flash controls the user may turn out not to have.
  if (role === null) return false
  return RANK[role] >= RANK[minimum]
}

/**
 * May act on observational data: create and edit ponds, annotations and manual
 * alerts, acknowledge alerts, trigger an ingest or a backfill.
 *
 * Backfill sits here rather than under `canAdminister` because roles rank by
 * irreversibility and a backfill destroys nothing — even though it is the most
 * expensive call in the product. Promoting someone to operator is therefore a
 * spending decision; see the comment at the route itself.
 */
export function canOperate(role: Role | null): boolean {
  return atLeast(role, 'operator')
}

/**
 * May change configuration or delete what cannot be rebuilt: alert rules, and
 * removing a pond along with its measurements, alerts and annotations.
 */
export function canAdminister(role: Role | null): boolean {
  return atLeast(role, 'admin')
}

/**
 * May this actor administer an account that currently holds `targetRole`?
 *
 * Mirrors `api/auth/user_admin_policy.py`: an administrative action reaches a
 * row only when the target ranks no higher than the actor. An admin manages
 * viewers, operators and other admins; the owner is out of their reach.
 *
 * ⚠️ THIS IS THE HALF THE UI CAN ANSWER, AND IT IS NOT THE WHOLE RULE. The
 * backend also refuses a user acting on their OWN row, and that fact is not
 * derivable from a pair of roles — the list ships `is_self` per row for exactly
 * that reason. A caller that hides controls on `!canActOn(...)` alone still
 * offers the admin the buttons that 403 on themselves; both conditions belong
 * in the guard.
 *
 * A null actor (session not yet resolved) can act on nobody — same reasoning as
 * `atLeast`: a control flashed and withdrawn is worse than one that arrives late.
 */
export function canActOn(actorRole: Role | null, targetRole: Role): boolean {
  if (actorRole === null) return false
  return RANK[targetRole] <= RANK[actorRole]
}

/**
 * May this actor hand out `newRole`?
 *
 * The escalation guard, mirroring `assert_may_grant` on the backend: nobody
 * grants a role above their own, so an admin can create and promote up to admin
 * and no further. Without it the role `<select>` lists `owner` to an admin, who
 * picks it and gets a 403 — the exact "the app is broken" reading this whole
 * module exists to avoid.
 *
 * Note this is deliberately about the role being GRANTED, not about the target.
 * Demoting an owner and promoting a viewer to owner are two different questions:
 * the first is `canActOn`, the second is this one. A screen that changes a role
 * asks both.
 */
export function canGrant(actorRole: Role | null, newRole: Role): boolean {
  if (actorRole === null) return false
  return RANK[newRole] <= RANK[actorRole]
}

/**
 * Every role this actor is allowed to grant, in ladder order.
 *
 * Derived from `canGrant` rather than re-deriving the comparison, so the option
 * list and the per-option check can never disagree. Exported because the
 * alternative — a component filtering a hard-coded role array — puts a second
 * copy of the ladder in a `.vue` file, where nothing tests it.
 */
export function grantableRoles(actorRole: Role | null): Role[] {
  return ROLE_LADDER.filter((role) => canGrant(actorRole, role))
}
